'use client'

// Portada de un local con platos Y súper (`storefrontKind === 'mixed'`, solo con online_retail_v2):
// pestañas «Restaurante | Mercado», cada una con solo lo suyo. Monta SOLO la pestaña activa:
// dos `MenuSection` a la vez duplicarían `id="menu"` y se pisarían `--ms-cats-h`.
import { useMemo, useState, type ReactNode } from 'react'
import { MenuSection, type MenuSectionProps } from './MenuSection'
import { StorefrontTabs } from './StorefrontTabs'
import {
  splitStorefrontCatalog,
  urlWithSection,
  STOREFRONT_TABS_STICKY_TOP,
  type StorefrontSection,
} from '@/lib/microsite/storefrontKind'

export type MixedStorefrontProps = {
  categories: MenuSectionProps['categories']
  /** Platos + previas de pasillo, tal como llegan; se separan por `is_retail`. */
  items: MenuSectionProps['items']
  initialSection: StorefrontSection
  /** Links «Ver todo el pasillo»: SOLO llegan al Mercado (una categoría mixta vista en
   *  Restaurante no lleva al pasillo del súper). */
  aisleHrefs?: Record<string, string>
  /** Buscador del súper: SOLO en el Mercado. */
  searchBox?: ReactNode
  /** Barra «¿Cómo lo recibes?»: arriba de las pestañas. */
  fulfillmentBar?: ReactNode
  menu: Omit<MenuSectionProps, 'categories' | 'items' | 'aisleHrefs' | 'stickyTop'>
}

export function MixedStorefront({
  categories,
  items,
  initialSection,
  aisleHrefs,
  searchBox,
  fulfillmentBar,
  menu,
}: MixedStorefrontProps) {
  const [section, setSection] = useState<StorefrontSection>(initialSection)
  const split = useMemo(() => splitStorefrontCatalog(categories, items), [categories, items])
  const active = section === 'market' ? split.market : split.dishes

  function changeSection(s: StorefrontSection) {
    setSection(s)
    // Solo la URL (recargar o compartir abre la misma pestaña); no navega por el servidor.
    // Next 16 integra history.replaceState con su router (docs: linking-and-navigating,
    // «Native History API»). urlWithSection conserva ?coupon, utm_* y el #hash.
    window.history.replaceState(null, '', urlWithSection(window.location.href, s))
  }

  return (
    <section id="storefront-tabs" className="scroll-mt-20">
      {fulfillmentBar}
      <StorefrontTabs active={section} onChange={changeSection} showWeightLine={split.market.hasWeighted} />
      {section === 'market' && searchBox}
      <MenuSection
        key={section}
        {...menu}
        categories={active.categories}
        items={active.items}
        aisleHrefs={section === 'market' ? aisleHrefs : undefined}
        stickyTop={STOREFRONT_TABS_STICKY_TOP}
      />
    </section>
  )
}
